import TagManager from '../../../../classes/TagManager';
import Button from '../../../atoms/button';
import Notification from '../../../atoms/notification/Notification';
import { Heading } from '../../../typography/heading/Heading';
import { loginSchema } from '../utils/validationSchemas';
import styles from './LoginPage.module.scss';
import app from '@frontastic/catwalk/src/js/app/app';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useMedia } from 'react-use';
import { formatErrorMessage } from '../login/utils';
import { useCsrfToken } from '../../../../utils/useCsrfToken';
import { useNostoContext } from '../../../../provider/nosto/NostoContext';
import PasswordField from '../../../atoms/passwordField';
import EmailField from '../../../atoms/emailField';
import { useYupValidationResolver } from '../../../../utils/useYupValidationResolver';
import clsx from 'clsx';
import { useRecaptcha } from '@srcDS/hooks/useRecaptcha';

const defaultTitle = 'Login';

const LoginPage = ({ onLogin = () => null, defaultValues, data }) => {
  const isMobile = useMedia('(max-width: 767px)', false);
  const resolver = useYupValidationResolver(loginSchema);
  const { register, handleSubmit, formState, setValue, getValues } = useForm({
    resolver,
    mode: 'all',
    defaultValues: defaultValues,
  });

  const { errors } = formState;

  const { omneo } = useSelector((state) => state['demo-common']);
  const { client } = omneo;
  const { nostoUpdateCustomer } = useNostoContext();

  const { executeRecaptcha, refetchRecaptcha } = useRecaptcha();
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [emailIsReady, setEmailIsReady] = useState(false);

  const [error, setError] = useState(null);
  const { csrfTokenComponent } = useCsrfToken('login', register, setValue);

  const handleReCaptchaVerify = React.useCallback(async () => {
    if (!executeRecaptcha) {
      console.log('Execute recaptcha not yet available');
      return;
    }
    const recTokenResult = await executeRecaptcha('login_account_form');
    setRecaptchaToken(recTokenResult);
    setValue('recaptcha_token', recTokenResult, { shouldDirty: false });
  }, [executeRecaptcha]);

  useEffect(() => {
    handleReCaptchaVerify();
  }, [executeRecaptcha]);

  const handleError = (error) => {
    setError(error);
    handleReCaptchaVerify();
  };

  const onSubmit = async (params) => {
    const { email, password, csrf_token, recaptcha_token: recaptchaToken, email_validation_code, email_validation_response } = params;

    let recaptcha_token = recaptchaToken;
    if (!recaptcha_token) {
      recaptcha_token = await refetchRecaptcha();
      if (!recaptcha_token) {
        console.error('Failed to get reCAPTCHA token');
        return;
      }
    }

    await onLogin({
      email,
      password,
      csrf_token,
      recaptcha_token,
      errorCallback: handleError,
      email_validation_code,
      email_validation_response,
    }).then(async (data) => {
      if (data?.loggedIn) {
        TagManager.loginEvent(data);
        if (client && client.ready) {
          client.get('profile').then((response) => {
            const comms = response?.body?.data?.attributes?.comms || [];
            nostoUpdateCustomer({
              marketingComms: comms?.email_optout ? false : true,
            });
          });
        }

        app.getRouter().history.replace('/');
      }
    });
  };

  const isBtnLoginDisabled = Boolean(!formState.isDirty || !formState.isValid || (getValues('email') && !emailIsReady));

  return (
    <div className={styles.loginPageContainer}>
      <Heading as="h4" align={isMobile ? 'left' : 'center'} className={styles.loginPageHeading}>
        {data.title || defaultTitle}
      </Heading>
      <form onSubmit={handleSubmit(onSubmit)} className={styles.loginPageForm}>
        {csrfTokenComponent}

        <input type="hidden" {...register('recaptcha_token', { value: recaptchaToken })} />
        <EmailField
          className={styles.textField}
          placeholder="Email Address"
          variant="underlined"
          name="email"
          isPureField
          register={register}
          setError={() => {}}
          setValue={setValue}
          setEmailIsReady={setEmailIsReady}
          errorMessage={errors?.email?.message}
          error={!!errors?.email}
        />
        <PasswordField
          onlyField
          hideNote
          placeholder="Password"
          variant="underlined"
          name="password"
          register={register}
          errorMessage={!!errors?.password?.message}
          error={!!errors?.password}
          customClasses={{
            iconAfter: styles.iconAfter,
            fieldControl: styles.fieldControl,
          }}
          intutStyle={styles.textField}
        />
        <Link className={styles.linkSetPassword} to="/forgot-password-page">
          Reset password
        </Link>

        {error && (
          <Notification className={styles.notificationMessage} type="error" message={formatErrorMessage(error)} />
        )}

        <Button
          disabled={isBtnLoginDisabled}
          className={clsx(styles.btnLogin, { [styles.btnLoginDisabled]: isBtnLoginDisabled })}
          type="submit"
        >
          Login
        </Button>
      </form>
    </div>
  );
};

LoginPage.propTypes = {
  onLogin: PropTypes.func,
  errorsFromBack: PropTypes.string,
};

export default LoginPage;
